import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, HeartHandshake, CheckCircle2, AlertTriangle, RotateCcw, Clock } from "lucide-react";

const usd = (c) => (c == null ? "$0.00" : `$${(c / 100).toFixed(2)}`);
const ACTIONS = [
  { value: "no_refund", label: "No refund (policy)" },
  { value: "partial_refund", label: "Partial / prorated refund" },
  { value: "full_refund", label: "Full refund" },
];

export default function AdminImmersionReviews() {
  const [token] = useState(() => getAdminToken());
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/immersion/reviews`, auth);
      setRows(data.reviews || []);
      setPending(data.pending || 0);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load reviews.");
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  const resolve = async (r) => {
    const d = drafts[r.id] || {};
    const action = d.action || "no_refund";
    const refundDollars = action === "no_refund" ? 0 : parseFloat(d.refund || "0") || 0;
    setBusy(r.id); setErr("");
    try {
      await axios.post(`${API}/admin/immersion/reviews/${r.id}/resolve`, {
        action, refund_cents: Math.round(refundDollars * 100), note: (d.note || "").trim(),
      }, auth);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Could not save."); } finally { setBusy(""); }
  };

  const reopen = async (r) => {
    setBusy(r.id);
    try { await axios.post(`${API}/admin/immersion/reviews/${r.id}/reopen`, {}, auth); await load(); }
    catch { setErr("Could not reopen."); } finally { setBusy(""); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "—"; } };

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-immersion-reviews">
      <div className="mx-auto max-w-4xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div>
          <h1 className="inline-flex items-center gap-2 font-display text-3xl text-ink">
            <HeartHandshake className="h-6 w-6 text-gold-deep" /> Immersion Reviews
          </h1>
          <p className="mt-1 text-sm text-ink-soft">
            Early Immersion cancellations land here for a mindful decision on prorations or partial refunds.
            {pending > 0 && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2.5 py-0.5 text-xs text-gold-deep" data-testid="reviews-pending-count"><AlertTriangle className="h-3.5 w-3.5" /> {pending} to review</span>}
          </p>
          <p className="mt-2 text-xs text-ink-muted">Recording a decision here keeps your records tidy. Issue the actual refund in your Stripe dashboard.</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 py-7 sm:px-8">
        {err && <p className="mb-4 text-sm text-terracotta" data-testid="reviews-error">{err}</p>}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl bg-ivory-50 p-10 text-center ring-1 ring-ivory-400/50" data-testid="reviews-empty">
            <CheckCircle2 className="mx-auto h-7 w-7 text-sage" />
            <p className="mt-3 font-display text-lg text-ink">Nothing to review</p>
            <p className="mt-1 text-sm text-ink-muted">Early Immersion cancellations will appear here if they happen.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => {
              const open = r.status === "needs_review";
              const d = drafts[r.id] || {};
              const action = d.action || "no_refund";
              return (
                <div key={r.id} data-testid={`review-${r.id}`}
                  className={`rounded-2xl bg-ivory-50 p-5 ring-1 ${open ? "ring-gold/40" : "ring-ivory-400/50"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-lg text-ink">{r.name || "Unnamed client"}</p>
                      <p className="text-sm text-ink-soft">{r.email || "—"}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] uppercase tracking-wide ${open ? "bg-gold/20 text-gold-deep" : "bg-sage/25 text-ink"}`}>
                      {open ? <><Clock className="h-3.5 w-3.5" /> Needs review</> : <><CheckCircle2 className="h-3.5 w-3.5" /> Resolved</>}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[["Months completed", `${r.months_completed ?? 0} of 8`], ["Paid to date", usd(r.amount_paid_cents)],
                      ["Cancelled", fmt(r.cancelled_at)], ["Reason", r.cancel_reason || "—"]].map(([k, v]) => (
                      <div key={k} className="rounded-lg bg-ivory-100 p-3 ring-1 ring-ivory-400/50">
                        <p className="text-[11px] uppercase tracking-wide text-ink-muted">{k}</p>
                        <p className="mt-0.5 text-sm text-ink">{v}</p>
                      </div>
                    ))}
                  </div>

                  {open ? (
                    <div className="mt-4 rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/50">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="text-sm text-ink-soft">Decision
                          <select value={action} onChange={(e) => setDraft(r.id, { action: e.target.value })} data-testid={`review-action-${r.id}`}
                            className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink">
                            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                          </select>
                        </label>
                        {action !== "no_refund" && (
                          <label className="text-sm text-ink-soft">Refund amount (USD)
                            <input type="number" min="0" step="0.01" value={d.refund || ""} onChange={(e) => setDraft(r.id, { refund: e.target.value })} data-testid={`review-refund-${r.id}`}
                              placeholder="e.g. 1600.00" className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink" />
                          </label>
                        )}
                      </div>
                      <label className="mt-3 block text-sm text-ink-soft">Note (internal)
                        <textarea value={d.note || ""} onChange={(e) => setDraft(r.id, { note: e.target.value })} data-testid={`review-note-${r.id}`} rows={2}
                          placeholder="What you decided and why…" className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink" />
                      </label>
                      <button onClick={() => resolve(r)} disabled={busy === r.id} data-testid={`review-resolve-${r.id}`}
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 hover:bg-ink/90 disabled:opacity-50">
                        {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Mark resolved
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-sage/10 p-4 ring-1 ring-sage/30">
                      <div className="text-sm text-ink">
                        <span className="font-display">{ACTIONS.find((a) => a.value === r.resolution_action)?.label || r.resolution_action}</span>
                        {r.resolution_refund_cents > 0 && <span className="ml-2 text-ink-soft">· {usd(r.resolution_refund_cents)} refunded</span>}
                        {r.resolution_note && <p className="mt-1 text-ink-soft">{r.resolution_note}</p>}
                        <p className="mt-1 text-xs text-ink-muted">Resolved {fmt(r.resolved_at)}</p>
                      </div>
                      <button onClick={() => reopen(r)} disabled={busy === r.id} data-testid={`review-reopen-${r.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-2 text-sm text-ink-soft hover:text-ink disabled:opacity-50">
                        <RotateCcw className="h-4 w-4" /> Reopen
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
